<script lang="ts" setup>
import { computed } from 'vue';

import type { SuggestionType } from '@apps/main/new-desktop/modules/suggestionTypeSelector';
import { useStatsigEventsStore } from '@apps/main/new-desktop/core/stores';
import {
  InfoIconPosition,
  SuggestionTypeSelector,
  SuggestionTypeSelectorSize,
} from '@apps/main/new-desktop/modules/suggestionTypeSelector';
import { FlightFilterType } from '@apps/main/new-desktop/core/stores/filters/flightFilter.model';
import {
  FilterType,
  FilterUpdateEventType,
} from '@apps/main/new-desktop/modules/filters';
import {
  FlightsFiltersEventType,
  FlightsFilterType,
} from '@apps/main/new-desktop/modules/flightsFilters';
import type { FlightsFilterEvents } from '@apps/main/new-desktop/modules/flightsFilters';
import { useFlightsFiltersStore } from '@apps/main/new-desktop/core/stores/filters/flightsFiltersStore';

import type { TransportType } from '@libs/trip-planner-interfaces';
import type { TagData } from '@libs/ui';
import { AppCheckbox, AppPreferencesTags, AppTrashCanButton } from '@libs/ui';

import type {
  AlternativeItineraryFlightSegment,
  AvailableItineraryFlightSegment,
  ItineraryFlightLoaderSegment,
  UnavailableItineraryFlightSegment,
} from '../../../models';

interface ExperimentalFlightSegmentHeaderProps {
  segment:
    | AvailableItineraryFlightSegment
    | AlternativeItineraryFlightSegment
    | UnavailableItineraryFlightSegment
    | ItineraryFlightLoaderSegment;
  showTrashBin?: boolean;
}
const props = defineProps<ExperimentalFlightSegmentHeaderProps>();

interface ExperimentalFlightSegmentHeaderEmits {
  (event: 'show-more-filters'): void;
  (event: 'suggestion-type-selected', suggestionType: SuggestionType): void;
  (event: 'select-transport', transportType: TransportType): void;
  (event: 'remove-filter', tagId: string): void;
  (event: 'remove-flight'): void;
}

const emit = defineEmits<ExperimentalFlightSegmentHeaderEmits>();

const statsigEventsStore = useStatsigEventsStore();
const flightsFiltersStore = useFlightsFiltersStore();

const directFlightOnly = computed<boolean>(() =>
  props.segment.filtersPills.some(
    (filter) =>
      filter.type === FlightFilterType.MaxNumberOfStops && filter.value === 0
  )
);

const filters = computed<TagData[]>(() =>
  props.segment.filtersPills.filter(
    (pill) =>
      pill.type !== FlightFilterType.MaxNumberOfStops || pill.value !== 0
  )
);

function onDirectFlightOnlyChange(value: boolean): void {
  statsigEventsStore.logTripOverviewDirectFlightOnlyCheckboxClicked();
  const flight: FlightsFilterEvents = {
    eventType: FlightsFiltersEventType.UpdateFilter,
    filterType: FlightsFilterType.NumberOfStops,
    data: {
      eventType: FilterUpdateEventType.UpdateFilter,
      filterType: FilterType.RadioButton,
      selected: 0,
      ingredientId: props.segment.ingredientId,
    },
  };
  if (value) {
    flightsFiltersStore.update(flight);
  } else {
    flight.data.selected = undefined;
    flightsFiltersStore.update(flight);
  }
}
</script>

<template>
  <div class="experimental-flight-segment-header">
    <div class="suggestion-selection">
      <SuggestionTypeSelector
        v-if="props.segment.suggestionType !== undefined"
        :suggestion-type="props.segment.suggestionType"
        icon-position="right"
        font-variant="font-bold"
        app-select-type="default"
        icon-name="list"
        :show-info-icon="InfoIconPosition.None"
        :size="SuggestionTypeSelectorSize.Normal"
        @suggestion-type-selected="emit('suggestion-type-selected', $event)"
      />
    </div>
    <div class="actions">
      <div class="direct-flight">
        <AppCheckbox
          :id="`direct-flight-mobile-${props.segment.ingredientId}`"
          size="extra-small"
          gap="spacing-8"
          class="mobile-checkbox"
          :checked="directFlightOnly"
          theme="secondary"
          @change="onDirectFlightOnlyChange"
        >
          {{ $t('filter.flightFilters.direct_flight_only') }}
        </AppCheckbox>
      </div>
      <div class="filters">
        <AppPreferencesTags
          :tags="filters"
          :total-tags="props.segment.filtersPills.length"
          :add-preferences-text="$t('filter.add_filters')"
          :show-filters-aria-label="$t('a11y.show_filters')"
          :remove-icon-aria-label="$t('a11y.remove_filter')"
          should-hide-add-button
          class="preferences-tags"
          @show-more-filters="emit('show-more-filters')"
          @add-filter="
            statsigEventsStore.logTripOverviewFilterButtonClicked();
            emit('show-more-filters');
          "
          @remove-filter="emit('remove-filter', $event)"
        />
      </div>
      <div v-if="props.showTrashBin" class="remove-flight">
        <AppTrashCanButton @click="emit('remove-flight')" />
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.experimental-flight-segment-header {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-start;
  gap: var(--spacing-8);
  margin-right: var(--spacing-8);

  .actions {
    display: flex;
    flex-direction: row;
    gap: var(--spacing-8);
    justify-content: space-between;
    align-items: center;
    .direct-flight {
      :deep(.label) {
        @include font-label-s-light;
      }
    }
  }
}
</style>
